import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

interface ParticleData {
  x: number;
  y: number;
  z: number;
  speed: number;
  scale: number;
  life: number;
}

type ParticleQuality = 'high' | 'low' | 'off';

interface ParticleProps {
  count?: number;
  bounds?: [number, number, number];
  quality?: ParticleQuality;
}

function getActualCount(count: number, quality: ParticleQuality = 'high'): number {
  if (quality === 'off') return 0;
  if (quality === 'low') return Math.max(1, Math.floor(count / 3));
  return count;
}

export function LavaParticles({ count = 20, bounds = [1.8, 0.2, 1.8] }: ParticleProps) {
  const mesh = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const particles = useMemo(() => {
    return new Array(count).fill(0).map((): ParticleData => ({
      x: (Math.random() - 0.5) * bounds[0],
      y: Math.random() * bounds[1],
      z: (Math.random() - 0.5) * bounds[2],
      speed: 0.5 + Math.random() * 1.5,
      scale: 0.2 + Math.random() * 0.3,
      life: Math.random(),
    }));
  }, [count, bounds]);

  useFrame((_, delta) => {
    if (!mesh.current) return;
    particles.forEach((p, i) => {
      p.life += delta * p.speed;
      if (p.life > 1) {
        p.life = 0;
        p.x = (Math.random() - 0.5) * bounds[0];
        p.z = (Math.random() - 0.5) * bounds[2];
      }

      const currentY = bounds[1] + p.life * 1.5;
      const currentScale = p.scale * (1 - p.life);

      dummy.position.set(p.x, currentY, p.z);
      dummy.scale.set(currentScale, currentScale, currentScale);
      dummy.updateMatrix();
      mesh.current!.setMatrixAt(i, dummy.matrix);
    });
    mesh.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, count]}>
      <sphereGeometry args={[0.2, 8, 8]} />
      <meshStandardMaterial color="#ffaa00" emissive="#ff4400" emissiveIntensity={2} toneMapped={false} />
    </instancedMesh>
  );
}

export function IceParticles({ count = 30, bounds = [1.8, 1.0, 1.8] }: ParticleProps) {
  const mesh = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const particles = useMemo(() => {
    return new Array(count).fill(0).map((): ParticleData => ({
      x: (Math.random() - 0.5) * bounds[0],
      y: Math.random() * bounds[1],
      z: (Math.random() - 0.5) * bounds[2],
      speed: 0.2 + Math.random() * 0.5,
      scale: 0.1 + Math.random() * 0.2,
      life: Math.random(),
    }));
  }, [count, bounds]);

  useFrame((_, delta) => {
    if (!mesh.current) return;
    particles.forEach((p, i) => {
      p.life += delta * p.speed;
      if (p.life > 1) {
        p.life = 0;
        p.x = (Math.random() - 0.5) * bounds[0];
        p.y = Math.random() * bounds[1];
        p.z = (Math.random() - 0.5) * bounds[2];
      }

      const currentY = p.y - p.life * 0.5;
      const currentScale = p.scale * Math.sin(p.life * Math.PI);

      dummy.position.set(p.x, currentY, p.z);
      dummy.rotation.set(p.life * Math.PI * 2, p.life * Math.PI * 2, 0);
      dummy.scale.set(currentScale, currentScale, currentScale);
      dummy.updateMatrix();
      mesh.current!.setMatrixAt(i, dummy.matrix);
    });
    mesh.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, count]}>
      <boxGeometry args={[0.2, 0.2, 0.2]} />
      <meshStandardMaterial color="#ffffff" emissive="#00ffff" emissiveIntensity={1} transparent opacity={0.6} />
    </instancedMesh>
  );
}

export function FireParticles({ count = 15, bounds = [0.8, 0.5, 0.8] }: ParticleProps) {
  const mesh = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const particles = useMemo(() => {
    return new Array(count).fill(0).map((): ParticleData => ({
      x: (Math.random() - 0.5) * bounds[0],
      y: Math.random() * bounds[1],
      z: (Math.random() - 0.5) * bounds[2],
      speed: 1.0 + Math.random() * 2.0,
      scale: 0.1 + Math.random() * 0.2,
      life: Math.random(),
    }));
  }, [count, bounds]);

  useFrame((_, delta) => {
    if (!mesh.current) return;
    particles.forEach((p, i) => {
      p.life += delta * p.speed;
      if (p.life > 1) {
        p.life = 0;
        p.x = (Math.random() - 0.5) * bounds[0];
        p.z = (Math.random() - 0.5) * bounds[2];
      }

      const currentY = p.y + p.life * 1.5;
      const currentScale = p.scale * (1 - p.life);

      dummy.position.set(p.x, currentY, p.z);
      dummy.rotation.set(p.life * Math.PI * 4, p.life * Math.PI * 4, 0);
      dummy.scale.set(currentScale, currentScale, currentScale);
      dummy.updateMatrix();
      mesh.current!.setMatrixAt(i, dummy.matrix);
    });
    mesh.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, count]}>
      <boxGeometry args={[0.2, 0.2, 0.2]} />
      <meshStandardMaterial color="#ffaa00" emissive="#ff2200" emissiveIntensity={2} toneMapped={false} />
    </instancedMesh>
  );
}

interface SparkleParticleProps extends ParticleProps {
  color?: string;
}

export function SparkleParticles({ count = 10, bounds = [1, 1, 1], color = "#ffff00" }: SparkleParticleProps) {
  const mesh = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const particles = useMemo(() => {
    return new Array(count).fill(0).map((): ParticleData => ({
      x: (Math.random() - 0.5) * bounds[0],
      y: (Math.random() - 0.5) * bounds[1],
      z: (Math.random() - 0.5) * bounds[2],
      speed: 0.5 + Math.random() * 1.0,
      scale: 0.05 + Math.random() * 0.1,
      life: Math.random(),
    }));
  }, [count, bounds]);

  useFrame((_, delta) => {
    if (!mesh.current) return;
    particles.forEach((p, i) => {
      p.life += delta * p.speed;
      if (p.life > 1) {
        p.life = 0;
        p.x = (Math.random() - 0.5) * bounds[0];
        p.y = (Math.random() - 0.5) * bounds[1];
        p.z = (Math.random() - 0.5) * bounds[2];
      }

      const currentScale = p.scale * Math.sin(p.life * Math.PI);

      dummy.position.set(p.x, p.y, p.z);
      dummy.rotation.set(p.life * Math.PI * 2, p.life * Math.PI * 2, 0);
      dummy.scale.set(currentScale, currentScale, currentScale);
      dummy.updateMatrix();
      mesh.current!.setMatrixAt(i, dummy.matrix);
    });
    mesh.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, count]}>
      <octahedronGeometry args={[0.2, 0]} />
      <meshStandardMaterial color={color} emissive={color} emissiveIntensity={2} toneMapped={false} />
    </instancedMesh>
  );
}

interface EnergyParticleProps {
  count?: number;
  bounds?: [number, number, number];
  color?: string;
  quality?: ParticleQuality;
}

export function EnergyParticles({ count = 15, bounds = [1.8, 1.5, 1.8], color = "#00ffff", quality = 'high' }: EnergyParticleProps) {
  const actualCount = getActualCount(count, quality);
  const mesh = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const particles = useMemo(() => {
    return new Array(actualCount).fill(0).map((): ParticleData => ({
      x: (Math.random() - 0.5) * bounds[0],
      y: Math.random() * bounds[1],
      z: (Math.random() - 0.5) * bounds[2],
      speed: 2.0 + Math.random() * 3.0,
      scale: 0.05 + Math.random() * 0.1,
      life: Math.random(),
    }));
  }, [actualCount, bounds]);

  useFrame((_, delta) => {
    if (!mesh.current || actualCount === 0) return;
    particles.forEach((p, i) => {
      p.life += delta * p.speed;
      if (p.life > 1) {
        p.life = 0;
        p.x = (Math.random() - 0.5) * bounds[0];
        p.y = Math.random() * bounds[1];
        p.z = (Math.random() - 0.5) * bounds[2];
      }

      const currentScale = p.scale * (Math.random() > 0.5 ? 1 : 0.2); // Flickering

      dummy.position.set(p.x, p.y, p.z);
      dummy.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);
      dummy.scale.set(currentScale, currentScale * 3, currentScale); // Stretched spark shape
      dummy.updateMatrix();
      mesh.current!.setMatrixAt(i, dummy.matrix);
    });
    mesh.current.instanceMatrix.needsUpdate = true;
  });

  if (actualCount === 0) return null;

  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, actualCount]}>
      <boxGeometry args={[0.1, 0.1, 0.1]} />
      <meshStandardMaterial color={color} emissive={color} emissiveIntensity={3} toneMapped={false} />
    </instancedMesh>
  );
}

interface VortexParticleProps {
  count?: number;
  radius?: number;
  color?: string;
  quality?: ParticleQuality;
}

interface VortexParticleData {
  angle: number;
  radius: number;
  y: number;
  speed: number;
  scale: number;
  life: number;
}

export function VortexParticles({ count = 30, radius = 0.8, color = "#ff00ff", quality = 'high' }: VortexParticleProps) {
  const actualCount = getActualCount(count, quality);
  const mesh = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const particles = useMemo(() => {
    return new Array(actualCount).fill(0).map((): VortexParticleData => ({
      angle: Math.random() * Math.PI * 2,
      radius: Math.random() * radius,
      y: Math.random() * 2,
      speed: 1.0 + Math.random() * 2.0,
      scale: 0.05 + Math.random() * 0.1,
      life: Math.random(),
    }));
  }, [actualCount, radius]);

  useFrame((_, delta) => {
    if (!mesh.current || actualCount === 0) return;
    particles.forEach((p, i) => {
      p.life += delta * p.speed * 0.5;
      p.angle += delta * p.speed * 2;

      if (p.life > 1) {
        p.life = 0;
        p.angle = Math.random() * Math.PI * 2;
        p.radius = Math.random() * radius;
      }

      const currentY = p.life * 2;
      const currentRadius = p.radius * (1 - p.life); // Pulls inward as it spirals up
      const currentScale = p.scale * Math.sin(p.life * Math.PI);

      dummy.position.set(Math.cos(p.angle) * currentRadius, currentY, Math.sin(p.angle) * currentRadius);
      dummy.rotation.set(0, p.angle, 0);
      dummy.scale.set(currentScale, currentScale, currentScale);
      dummy.updateMatrix();
      mesh.current!.setMatrixAt(i, dummy.matrix);
    });
    mesh.current.instanceMatrix.needsUpdate = true;
  });

  if (actualCount === 0) return null;

  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, actualCount]}>
      <sphereGeometry args={[0.2, 8, 8]} />
      <meshStandardMaterial color={color} emissive={color} emissiveIntensity={2} toneMapped={false} />
    </instancedMesh>
  );
}
